'use client';

import { useQuery } from '@tanstack/react-query';

interface FeatureFlags {
  chatUsersEnabled: boolean;
  chatMaxUsers: number;
  chatMultilingualEnabled: boolean;
  chatHideVectorizeEnabled: boolean;
  chatFileUploadEnabled: boolean;
  chatVoiceModelsEnabled: boolean;
  chatSSOEnabled: boolean;
  chat2FAEnabled: boolean;
  chatWebhooksEnabled: boolean;
  chatThemingEnabled: boolean;
  chatFeedbackEnabled: boolean;
  chatSafetyEnabled: boolean;
  chatAiConsentEnabled: boolean;
  chatToolsEnabled: boolean;
}

interface UseFeatureFlagsOptions {
  enabled?: boolean;
}

export function useFeatureFlags(agentId?: string, options?: UseFeatureFlagsOptions) {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<{ flags: FeatureFlags }>({
    queryKey: ['featureFlags', agentId],
    queryFn: async () => {
      if (!agentId) {
        // Return default flags when no agentId
        return {
          flags: {
            chatUsersEnabled: true,
            chatMaxUsers: 100,
            chatMultilingualEnabled: true,
            chatHideVectorizeEnabled: true,
            chatFileUploadEnabled: true,
            chatVoiceModelsEnabled: true,
            chatSSOEnabled: true,
            chat2FAEnabled: true,
            chatWebhooksEnabled: true,
            chatThemingEnabled: true,
            chatFeedbackEnabled: true,
            chatSafetyEnabled: true,
            chatAiConsentEnabled: false,
            chatToolsEnabled: false,
          }
        };
      }

      const response = await fetch('/api/feature-flags', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentId }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch feature flags');
      }

      return response.json();
    },
    // Cache for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Keep in cache for 10 minutes
    gcTime: 10 * 60 * 1000,
    // Always retry on error
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    enabled: options?.enabled !== false,
  });

  const flags = data?.flags || {
    chatUsersEnabled: true,
    chatMaxUsers: 100,
    chatMultilingualEnabled: true,
    chatHideVectorizeEnabled: true,
    chatFileUploadEnabled: true,
    chatVoiceModelsEnabled: true,
    chatSSOEnabled: true,
    chat2FAEnabled: true,
    chatWebhooksEnabled: true,
    chatThemingEnabled: true,
    chatFeedbackEnabled: true,
    chatSafetyEnabled: true,
    chatAiConsentEnabled: false,
    chatToolsEnabled: false,
  };

  return {
    flags,
    chatUsersEnabled: flags.chatUsersEnabled ?? false,
    chatMaxUsers: flags.chatMaxUsers ?? 100,
    chatMultilingualEnabled: flags.chatMultilingualEnabled ?? false,
    chatHideVectorizeEnabled: flags.chatHideVectorizeEnabled ?? false,
    chatFileUploadEnabled: flags.chatFileUploadEnabled ?? false,
    chatVoiceModelsEnabled: flags.chatVoiceModelsEnabled ?? false,
    chatSSOEnabled: flags.chatSSOEnabled ?? false,
    chat2FAEnabled: flags.chat2FAEnabled ?? false,
    chatWebhooksEnabled: flags.chatWebhooksEnabled ?? false,
    chatThemingEnabled: flags.chatThemingEnabled ?? false,
    chatFeedbackEnabled: flags.chatFeedbackEnabled ?? false,
    chatSafetyEnabled: flags.chatSafetyEnabled ?? false,
    chatAiConsentEnabled: flags.chatAiConsentEnabled ?? false,
    chatToolsEnabled: flags.chatToolsEnabled ?? false,
    isReady: !isLoading,
    isLoading,
    isError,
    error,
    refetch,
  };
}